import {TicketRepository} from "@api/repositories/TicketRepository";
import {ObjectRepository} from "@api/repositories/ObjectRepository";
import {RepositoryFabric} from "@api/repositories/RepositoryFabric";
import {Ticket} from "@api/models/tickets/Ticket";
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";
import {UserRepository} from '@api/repositories/UserRepository';
import {repositories} from "@service/api/Repositories";
import {UserProfile} from "@api/models/UserProfile";
import {TicketType} from "@share/ticketSystem/enums/TicketTypes";
import {Store} from "vuex";
import { CancelToken } from "axios";
import { Idea } from "@api/models/tickets/Idea";
import { TicketStatus } from '@api/models/TicketStatus';
import { TicketStatusCode } from "@share/ticketSystem/enums/TicketStatusCode";

export class TicketService {
    static TICKET_STATUSES_COLLECTION = 'TicketStatuses';
    static TICKET_CATEGORIES_COLLECTION = 'TicketCategories';
    ticketRepository: TicketRepository;
    objectRepository: ObjectRepository;
    userRepository: UserRepository;
    repositories: RepositoryFabric;
    store: Store<any>;
    isAdmin: boolean;

    constructor(store: Store<any>, isAdmin = false) {
        this.store = store;
        this.repositories = repositories;
        this.ticketRepository = this.repositories.get(RepositoryFabric.TICKET);
        this.objectRepository = this.repositories.get(RepositoryFabric.OBJECT);
        this.userRepository = this.repositories.get(RepositoryFabric.USER);
        this.isAdmin = isAdmin;
    }

    getTickets(type: TicketType, query: QueryParametersInterface = {}, config = {}) {
        try {
            if (this.isAdmin) {
                return this.ticketRepository.query(type, query, config);
            }
            return this.ticketRepository.list(type, false, query, true, config);
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    getTicketsWithCount(type: TicketType, query: QueryParametersInterface = {}, cancelToken: CancelToken, onlyOwn = false) {
        try {
            if (this.isAdmin) {
                return this.ticketRepository.queryWithCount(type, query, { cancelToken });
            }
            return this.ticketRepository.list(type, onlyOwn, query, true);
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    getTicket(type: TicketType, id: string) {
        try {
            return this.ticketRepository.find(type, id);
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    async getTicketById(type: TicketType, id: string, query: QueryParametersInterface = {}) {
        try {
            if (!this.isAdmin) {
                return this.ticketRepository.find(type, id);
            }

            let tickets = await this.ticketRepository.query(type, {
                take: 1,
                skip: 0,
                where: {
                    id: id
                },
                ...query
            });
            if (tickets && tickets.length) {
                return tickets.shift();
            }
            return null
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
            return null
        }
    }

    updateTicket(type: TicketType, id: string, changes: any) {
        try {
            return this.ticketRepository.update(type, id, changes);
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    async createTicket(type: TicketType, data: any, ownerId: number | null) {
        if (this.isAdmin) {
            return await this.ticketRepository.register(type, data, ownerId);
        }
        return this.ticketRepository.create(type, data);
    }

    sendTicketsToEmail(type: TicketType, subject = "", query: QueryParametersInterface = {}) {
        return this.ticketRepository.queryEmail(type, subject, query);
    }

    sendTicketsCountToEmail(type: TicketType, subject = "", query: QueryParametersInterface = {}) {
        return this.ticketRepository.groupCountQueryEmail(type, subject, query);
    }

    sendGivenAndAcceptedToEmail(type: TicketType, subject = "", query: QueryParametersInterface = {}) {
        const statusCode = type === TicketType.idea ? `${TicketStatusCode.ideas}.inProgress` : `${TicketStatusCode.problems}.inProgress`;
        return this.ticketRepository.givenAndAcceptedQueryEmail(type, subject, statusCode, query);
    }

    sendWeeklyTicketsToEmail(type: TicketType, params: any, query: QueryParametersInterface = {}) {
        return this.ticketRepository.groupedByWeekQueryEmail(type, params, query);
    }

    /**
     * TODO: BAD METHOD
     * @param tickets
     * @param query
     * @param cancelToken
     * @returns
     */
    async getTicketsUsers(tickets: (Ticket | Idea)[] | null, query: QueryParametersInterface = {}, cancelToken?: CancelToken): Promise<UserProfile[]> {
        let usersIds: number[] = [];
        let users: UserProfile[] = [];
        if (tickets) {

            tickets.forEach((ticket: Ticket) => {
                if (ticket.ownerIds && ticket.ownerIds.length) {
                    usersIds.push(...ticket.ownerIds);
                }
                if (ticket.responsibleUserId) {
                    usersIds.push(ticket.responsibleUserId);
                }

                if (ticket.responsibleForImplementationUserId) {
                    usersIds.push(ticket.responsibleForImplementationUserId);
                }
            });

            if (usersIds.length) {
                usersIds = [...new Set(usersIds)];
                let profiles = undefined;

                try {
                    profiles = await this.userRepository.profiles({
                        where: {
                            userId: {
                                '$in': usersIds
                            }
                        },
                        ...query
                    }, { cancelToken });
                } catch (e) {
                    this.store.dispatch('reportException', e, { root: true });
                }

                if (profiles && profiles.length) {
                    users = profiles
                }
            }
        }

        return users;
    }

    async getUsers(query: QueryParametersInterface = {}): Promise<{ [key: string]: UserProfile }> {
        let users: { [key: string]: UserProfile } = {};
        let profiles = undefined;

        try {
            profiles = await this.userRepository.profiles({
                ...query
            });
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }

        if (profiles && profiles.length) {
            profiles.forEach((profile) => {
                if (profile.userId) {
                    users[`${profile.userId}`] = profile;
                }
            });
        }

        return users;
    }

    getTicketsRefCollections(
        schema: string,
        query: QueryParametersInterface = {}
    ) {
        try {
            return this.objectRepository.query(schema, {
                ...query
            });
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    getCategories(ticketType: TicketType, query: QueryParametersInterface = {}) {
        try{
            return this.objectRepository.query(
                TicketService.TICKET_CATEGORIES_COLLECTION,
                {
                    include: ["id", "title"],
                    skip: 0,
                    take: -1,
                    where: {
                        type: ticketType
                    },
                    order: [
                        {
                            column: 'title',
                            direction: 'asc'
                        }
                    ],
                    ...query
                }
            );
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    getStatuses(ticketType: TicketType, query: QueryParametersInterface = {}): Promise<TicketStatus[]> | undefined {
        try {
            return this.objectRepository.query(
                TicketService.TICKET_STATUSES_COLLECTION,
                {
                    include: ["id", "title", "code", "color", "iconFileId"],
                    skip: 0,
                    take: -1,
                    where: {
                        parentId: {
                            $inQuery: {
                                schema: TicketService.TICKET_STATUSES_COLLECTION,
                                where: {
                                    code: ticketType + 's'
                                }
                            }
                        }
                    },
                    order: [
                        {
                            column: 'title',
                            direction: 'asc'
                        }
                    ],
                    ...query
                }
            );
        } catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
    }

    getStatistic(type: TicketType, queryBody: any) {
        return this.ticketRepository.getStatictic(type, queryBody);
    }
}
